import * as React from "react";
import { observer } from "mobx-react-lite";
import UsersManagerStore from "../stores/UsersManagerStore";
import { Checkbox, Select, Table, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { ColumnProps } from "antd/lib/table";
import { AppRoles } from "src/modules/authorization/Permissions";

type Props = {
    store: UsersManagerStore;
};

type ProjectRoleModel = {
    id: string;
    projectName: string;
    isAdmin: boolean;
    roles: string[];
};

export const ProjectsAssignmentTable: React.FC<Props> = observer(
    ({ store }) => {
        const isEditingDisabled = (projectId: string) => {
            const editableUser = store.editingAssignmentsFor;
            return editableUser
                ? store.projects.some(
                      (p) => p.id === projectId && editableUser === p.owner,
                  )
                : false;
        };
        const columns: ColumnProps<ProjectRoleModel>[] = [
            {
                title: "Project name",
                dataIndex: "projectName",
            },
            {
                title: (
                    <div
                        style={{
                            display: "flex",
                            gap: 5,
                        }}
                    >
                        Is Admin
                        <Tooltip title="Project Admin can manage templates, widgets, metadata, task types, actions, and statuses for this project.">
                            <InfoCircleOutlined />
                        </Tooltip>
                    </div>
                ),
                dataIndex: "isAdmin",
                width: 110,
                render: (isAdmin: boolean, record: ProjectRoleModel) => {
                    return (
                        <Checkbox
                            checked={isAdmin}
                            onChange={(e) =>
                                store.setAdminFlagForProject(
                                    record.id,
                                    e.target.checked,
                                )
                            }
                            disabled={isEditingDisabled(record.id)}
                        />
                    );
                },
            },
            {
                title: (
                    <div
                        style={{
                            display: "flex",
                            gap: 5,
                        }}
                    >
                        Action roles
                        <Tooltip title="Assign optional action roles to this user for the selected project.">
                            <InfoCircleOutlined />
                        </Tooltip>
                    </div>
                ),
                dataIndex: "roles",
                width: 300,
                render: (roles: string[], record: ProjectRoleModel) => (
                    <Select
                        style={{ width: "100%" }}
                        mode="multiple"
                        options={store.getActionRoleOptions(record.id)}
                        value={
                            store.isEditableUserProjectAdmin(record.id)
                                ? null
                                : roles
                        }
                        onChange={(values) =>
                            store.setUserRolesForProject(record.id, values)
                        }
                        placeholder="Add roles"
                        disabled={
                            isEditingDisabled(record.id) ||
                            store.isEditableUserProjectAdmin(record.id)
                        }
                    />
                ),
            },
        ];

        if (store.currentEditableRole === AppRoles.admin) {
            return (
                <div>
                    System admin has full access to all projects. Project-level
                    configuration is not required.
                </div>
            );
        }

        return (
            <Table
                className="alpha-table"
                columns={columns}
                dataSource={store.projects
                    .map((s) => ({
                        id: s.id,
                        projectName: s.name,
                        isAdmin:
                            store.editableProjectsForUser.find(
                                (e) => e.projectId === s.id,
                            )?.isAdmin ?? false,
                        roles:
                            store.editableProjectsForUser.find(
                                (e) => e.projectId === s.id,
                            )?.roles ?? [],
                    }))
                    .sort((a, b) => a.projectName.localeCompare(b.projectName))}
                scroll={{ y: "calc(100vh - 250px)" }}
                rowKey={(s) => s.id}
                pagination={false}
                rowSelection={{
                    selectedRowKeys: store.editableProjectsForUser.map(
                        (p) => p.projectId,
                    ),
                    onChange: (selectedRowKeys) => {
                        store.setAssignedProjectsForUser(
                            selectedRowKeys as string[],
                        );
                    },
                    getCheckboxProps: (record: ProjectRoleModel) => {
                        return {
                            disabled: isEditingDisabled(record.id),
                        };
                    },
                }}
            />
        );
    },
);
